// src/encounter/intelligence/rail-menu.tsx
//
// The rail's small action menus (the Next step "More" and the footer's body-system choice), built on the Signal DS
// AnchoredPopover, the popover the app's other menus use (calendar Create, account menu). The DS popover moves focus
// into the menu, closes on Escape and on an outside click, and returns focus to the trigger; this adds the menu keys
// (ArrowDown / ArrowUp wrap, Home / End) and ArrowDown on the closed trigger. The surface portals into a positioned host
// beside the trigger, not <body>: under the app's root zoom a body portal lands the surface away from its trigger (see
// the DS note on AnchoredPopoverContent `container`). Words come from the caller; nothing here logs.
import { cloneElement, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverItem, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';

export interface RailMenuItem {
  key: string;
  label: string;
  onPick: () => void;
  /** Optional data-action on the item (tests and analytics-free hooks). */
  action?: string;
}

export interface RailMenuProps {
  /** The menu's accessible name. */
  label: string;
  /** The trigger: a <button> element (its words or glyph, its classes). */
  trigger: ReactElement<Record<string, unknown>>;
  items: readonly RailMenuItem[];
  /** Drawn and inert: the trigger alone, disabled, with no menu. */
  disabled?: boolean;
  side?: 'top' | 'bottom';
  align?: 'start' | 'end';
  hostClassName: string;
  menuClassName: string;
  itemClassName: string;
}

const KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

export function RailMenu({ label, trigger, items, disabled = false, side = 'bottom', align = 'end', hostClassName, menuClassName, itemClassName }: RailMenuProps) {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLSpanElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);
  if (disabled) return <span className={hostClassName}>{cloneElement(trigger, { disabled: true, 'aria-haspopup': 'menu', 'aria-expanded': false })}</span>;
  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!KEYS.has(e.key)) return;
    const list = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'));
    if (!list.length) return;
    e.preventDefault();
    const i = list.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1 : e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
    list[next].focus();
  };
  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };
  return (
    <span ref={hostRef} className={hostClassName}>
      <AnchoredPopover open={open} onOpenChange={setOpen}>
        <AnchoredPopoverTrigger render={cloneElement(trigger, { 'aria-haspopup': 'menu', 'aria-expanded': open, onKeyDown: onTriggerKey })} />
        <AnchoredPopoverContent container={hostRef} side={side} align={align} role="menu" aria-label={label} className={menuClassName} initialFocus={firstRef} onKeyDown={onMenuKey}>
          {items.map((it, i) => (
            <AnchoredPopoverItem
              key={it.key}
              ref={i === 0 ? firstRef : undefined}
              role="menuitem"
              tabIndex={-1}
              className={itemClassName}
              data-action={it.action}
              onClick={() => {
                setOpen(false);
                it.onPick();
              }}
            >
              {it.label}
            </AnchoredPopoverItem>
          ))}
        </AnchoredPopoverContent>
      </AnchoredPopover>
    </span>
  );
}
